import { useState, useEffect, useRef, useCallback } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { storage } from './lib/storage';
import { generateScramble, CubeState, formatTime, calculateStats, calculateBestAverages } from './lib/cubeUtils';
import { algorithms, getPrimaryAlgorithm } from './lib/algorithms';
import CrossTrainer from './CrossTrainer';

// ============================================
// CUBE VISUALIZATION COMPONENT (Full Net)
// ============================================
function CubeVisualization({ scramble }) {
  const cube = new CubeState();
  if (scramble) {
    cube.applyScramble(scramble);
  }

  const colorMap = {
    'W': 'sticker-white',
    'Y': 'sticker-yellow',
    'G': 'sticker-green',
    'B': 'sticker-blue',
    'R': 'sticker-red',
    'O': 'sticker-orange'
  };

  const renderFace = (face, startCol, startRow) => {
    return face.map((color, idx) => {
      const row = Math.floor(idx / 3);
      const col = idx % 3;
      return (
        <div
          key={`${startCol}-${startRow}-${idx}`}
          className={`cube-sticker ${colorMap[color]}`}
          style={{
            gridColumn: startCol + col + 1,
            gridRow: startRow + row + 1
          }}
        />
      );
    });
  };

  return (
    <div className="cube-visualization">
      <div className="cube-net">
        {renderFace(cube.faces.U, 3, 0)}
        {renderFace(cube.faces.L, 0, 3)}
        {renderFace(cube.faces.F, 3, 3)}
        {renderFace(cube.faces.R, 6, 3)}
        {renderFace(cube.faces.B, 9, 3)}
        {renderFace(cube.faces.D, 3, 6)}
      </div>
    </div>
  );
}

// ============================================
// CUBE FACE COMPONENT (For Algorithm Cards)
// ============================================
function CubeFace({ pattern, size = 60, type = 'oll' }) {
  if (!pattern) return null;
  
  const getColor = (char, type) => {
    if (type === 'oll') {
      return char === 'Y' ? '#ffd500' : '#888';
    } else if (type === 'pll') {
      // Standard cube colors: F=Green, B=Blue, R=Red, L=Orange
      const colors = { 
        'G': '#00d158', // Green (Front)
        'B': '#0046ad', // Blue (Back)
        'R': '#dc2626', // Red (Right)
        'O': '#ff5800', // Orange (Left)
        'Y': '#ffd500', // Yellow (for center)
      };
      return colors[char] || '#888';
    } else if (type === 'f2l') {
      const colors = { 'R': '#dc2626', 'B': '#0046ad', 'O': '#ff5800', 'Y': '#ffd500', 'W': '#ffffff', 'G': '#00d158', 'X': '#444' };
      return colors[char] || '#888';
    }
    return '#888';
  };

  if (type === 'pll') {
    // PLL pattern: 12 chars = Top(3) + Right(3) + Bottom(3) + Left(3)
    // Each group is: corner, edge, corner going clockwise
    // View is looking down at U face, F face toward bottom
    const stickers = pattern.split('');

    // Grid-based layout like a proper cube top view
    const cornerSize = 7;
    const edgeSize = 10;

    return (
      <svg width={size} height={size} viewBox="0 0 60 60">
        {/* Background */}
        <rect x="0" y="0" width="60" height="60" fill="transparent"/>

        {/* Yellow center - 3x3 grid */}
        <rect x="15" y="15" width="30" height="30" fill="#ffd500" rx="2" stroke="rgba(0,0,0,0.15)" strokeWidth="0.8"/>

        {/* Dividing lines on yellow center for 3x3 grid */}
        <line x1="25" y1="15" x2="25" y2="45" stroke="rgba(0,0,0,0.15)" strokeWidth="0.8"/>
        <line x1="35" y1="15" x2="35" y2="45" stroke="rgba(0,0,0,0.15)" strokeWidth="0.8"/>
        <line x1="15" y1="25" x2="45" y2="25" stroke="rgba(0,0,0,0.15)" strokeWidth="0.8"/>
        <line x1="15" y1="35" x2="45" y2="35" stroke="rgba(0,0,0,0.15)" strokeWidth="0.8"/>

        {/* Top edge (Back) - index 0,1,2 */}
        {/* Top-left corner */}
        <rect x="15" y="6" width={cornerSize} height={cornerSize} fill={getColor(stickers[0], 'pll')} rx="1.5" stroke="rgba(0,0,0,0.25)" strokeWidth="0.8"/>
        {/* Top edge */}
        <rect x="25" y="6" width={edgeSize} height={cornerSize} fill={getColor(stickers[1], 'pll')} rx="1.5" stroke="rgba(0,0,0,0.25)" strokeWidth="0.8"/>
        {/* Top-right corner */}
        <rect x="38" y="6" width={cornerSize} height={cornerSize} fill={getColor(stickers[2], 'pll')} rx="1.5" stroke="rgba(0,0,0,0.25)" strokeWidth="0.8"/>

        {/* Right edge (Right) - index 3,4,5 */}
        {/* Right-top corner */}
        <rect x="47" y="15" width={cornerSize} height={cornerSize} fill={getColor(stickers[3], 'pll')} rx="1.5" stroke="rgba(0,0,0,0.25)" strokeWidth="0.8"/>
        {/* Right edge */}
        <rect x="47" y="25" width={cornerSize} height={edgeSize} fill={getColor(stickers[4], 'pll')} rx="1.5" stroke="rgba(0,0,0,0.25)" strokeWidth="0.8"/>
        {/* Right-bottom corner */}
        <rect x="47" y="38" width={cornerSize} height={cornerSize} fill={getColor(stickers[5], 'pll')} rx="1.5" stroke="rgba(0,0,0,0.25)" strokeWidth="0.8"/>

        {/* Bottom edge (Front) - index 6,7,8 */}
        {/* Bottom-right corner */}
        <rect x="38" y="47" width={cornerSize} height={cornerSize} fill={getColor(stickers[6], 'pll')} rx="1.5" stroke="rgba(0,0,0,0.25)" strokeWidth="0.8"/>
        {/* Bottom edge */}
        <rect x="25" y="47" width={edgeSize} height={cornerSize} fill={getColor(stickers[7], 'pll')} rx="1.5" stroke="rgba(0,0,0,0.25)" strokeWidth="0.8"/>
        {/* Bottom-left corner */}
        <rect x="15" y="47" width={cornerSize} height={cornerSize} fill={getColor(stickers[8], 'pll')} rx="1.5" stroke="rgba(0,0,0,0.25)" strokeWidth="0.8"/>

        {/* Left edge (Left) - index 9,10,11 */}
        {/* Left-bottom corner */}
        <rect x="6" y="38" width={cornerSize} height={cornerSize} fill={getColor(stickers[9], 'pll')} rx="1.5" stroke="rgba(0,0,0,0.25)" strokeWidth="0.8"/>
        {/* Left edge */}
        <rect x="6" y="25" width={cornerSize} height={edgeSize} fill={getColor(stickers[10], 'pll')} rx="1.5" stroke="rgba(0,0,0,0.25)" strokeWidth="0.8"/>
        {/* Left-top corner */}
        <rect x="6" y="15" width={cornerSize} height={cornerSize} fill={getColor(stickers[11], 'pll')} rx="1.5" stroke="rgba(0,0,0,0.25)" strokeWidth="0.8"/>
      </svg>
    );
  }

  // OLL and F2L - 3x3 grid
  const cells = pattern.split('');
  return (
    <svg width={size} height={size} viewBox="0 0 60 60">
      {cells.map((char, idx) => {
        const row = Math.floor(idx / 3);
        const col = idx % 3;
        return (
          <rect
            key={idx}
            x={col * 20 + 1}
            y={row * 20 + 1}
            width="18"
            height="18"
            fill={getColor(char, type)}
            rx="2"
            stroke="rgba(0,0,0,0.2)"
            strokeWidth="1"
          />
        );
      })}
    </svg>
  );
}

// ============================================
// NOTATION GUIDE COMPONENT
// ============================================
function NotationGuide() {
  const [isExpanded, setIsExpanded] = useState(false);

  const basicMoves = [
    { move: 'R', name: 'Right', description: 'Turn the right face 90° clockwise', color: '#ef4444' },
    { move: 'L', name: 'Left', description: 'Turn the left face 90° clockwise', color: '#f97316' },
    { move: 'U', name: 'Up', description: 'Turn the top face 90° clockwise', color: '#eab308' },
    { move: 'D', name: 'Down', description: 'Turn the bottom face 90° clockwise', color: '#22c55e' },
    { move: 'F', name: 'Front', description: 'Turn the front face 90° clockwise', color: '#3b82f6' },
    { move: 'B', name: 'Back', description: 'Turn the back face 90° clockwise', color: '#a855f7' },
  ];

  const modifiers = [
    { symbol: "'", name: 'Prime/Inverse', example: "R'", description: 'Counter-clockwise turn (opposite direction)' },
    { symbol: '2', name: 'Double', example: 'R2', description: 'Turn the face 180° (two quarter turns)' },
  ];

  const advancedMoves = [
    { move: 'M', description: 'Middle layer (between R and L), follows L direction' },
    { move: 'E', description: 'Equator layer (between U and D), follows D direction' },
    { move: 'S', description: 'Standing layer (between F and B), follows F direction' },
    { move: 'r', description: 'Wide R - turn R and M together (2 layers)' },
    { move: 'l', description: 'Wide L - turn L and M together (2 layers)' },
    { move: 'x', description: 'Rotate entire cube on R axis' },
    { move: 'y', description: 'Rotate entire cube on U axis' },
    { move: 'z', description: 'Rotate entire cube on F axis' },
  ];

  return (
    <div className="notation-guide">
      <button className="notation-guide-toggle" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="notation-guide-header">
          <span className="notation-guide-icon">📖</span>
          <div className="notation-guide-title">
            <div className="notation-guide-name">Cubing Notation Guide</div>
            <div className="notation-guide-subtitle">New to algorithms? Learn how to read cube notation</div>
          </div>
        </div>
        <span className={`notation-guide-arrow ${isExpanded ? 'expanded' : ''}`}>▼</span>
      </button>

      {isExpanded && (
        <div className="notation-guide-content">
          <div className="notation-intro">
            <p>Cube notation uses letters to represent moves. Each letter is a face of the cube, 
            and modifiers tell you how to turn that face. <strong>All moves are described as if you're looking at that face.</strong></p>
          </div>

          <h4 className="notation-section-title">🎯 The 6 Basic Moves</h4>
          <div className="notation-grid">
            {basicMoves.map((m, i) => (
              <div key={i} className="notation-card" style={{ borderColor: `${m.color}40` }}>
                <div className="notation-move" style={{ color: m.color }}>{m.move}</div>
                <div className="notation-name">{m.name}</div>
                <div className="notation-desc">{m.description}</div>
              </div>
            ))}
          </div>

          <h4 className="notation-section-title">🔄 Modifiers</h4>
          <div className="notation-modifiers">
            {modifiers.map((m, i) => (
              <div key={i} className="notation-modifier">
                <div className="modifier-example">{m.example}</div>
                <div className="modifier-info">
                  <strong>{m.name}</strong>
                  <span>{m.description}</span>
                </div>
              </div>
            ))}
          </div>

          <h4 className="notation-section-title">⚡ Advanced Moves</h4>
          <div className="notation-advanced">
            {advancedMoves.map((m, i) => (
              <div key={i} className="notation-advanced-item">
                <span className="advanced-move">{m.move}</span>
                <span className="advanced-desc">{m.description}</span>
              </div>
            ))}
          </div>

          <div className="notation-tips">
            <h4>💡 Tips for Learning</h4>
            <ul>
              <li>Practice each move slowly until it becomes automatic</li>
              <li>Learn algorithms in small chunks (3-4 moves at a time)</li>
              <li>Use your fingers efficiently - minimize hand repositioning</li>
              <li>Practice slowly first - speed comes with muscle memory</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// ALGORITHM TRAINER MODAL
// ============================================
function AlgorithmTrainer({ algorithm, category, onClose, algTimes, setAlgTimes }) {
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const [currentScramble, setCurrentScramble] = useState('');
  const [spaceProcessed, setSpaceProcessed] = useState(false);
  const timerRef = useRef(null);
  const holdTimerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Get the selected variant or fall back to primary/old format
  const selectedVariant = algorithm.selectedVariant || 
    (algorithm.algorithms && algorithm.algorithms[0]) || 
    { solution: algorithm.solution || algorithm.moves, scramble: algorithm.scramble };

  const algData = algTimes[algorithm.name] || { times: [] };
  const times = algData.times || [];

  const generateTrainingScramble = useCallback(() => {
    const aufOptions = ['', 'U ', "U' ", 'U2 '];
    const preAuf = aufOptions[Math.floor(Math.random() * aufOptions.length)];
    const postAuf = aufOptions[Math.floor(Math.random() * aufOptions.length)];
    const baseScramble = selectedVariant.scramble || algorithm.scramble;
    const scramble = preAuf + baseScramble + (postAuf ? ' ' + postAuf.trim() : '');
    setCurrentScramble(scramble.trim());
  }, [selectedVariant, algorithm]);

  useEffect(() => {
    generateTrainingScramble();
  }, [generateTrainingScramble]);

  useEffect(() => {
    if (isRunning) {
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setTime(Date.now() - startTimeRef.current);
      }, 10);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isRunning]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (e.repeat) return;

        if (isRunning && !spaceProcessed) {
          setIsRunning(false);
          saveTime(Date.now() - startTimeRef.current);
          setSpaceProcessed(true);
        } else if (!isHolding && !isRunning) {
          setIsHolding(true);
          setSpaceProcessed(false);
          holdTimerRef.current = setTimeout(() => {
            setTime(0);
            startTimeRef.current = Date.now();
            setIsRunning(true);
            setIsHolding(false);
            setSpaceProcessed(true);
          }, 300);
        }
      } else if (e.code === 'Escape') {
        onClose();
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space') {
        setSpaceProcessed(false);
        if (isHolding && !isRunning) {
          clearTimeout(holdTimerRef.current);
          setIsHolding(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      clearTimeout(holdTimerRef.current);
    };
  }, [isRunning, isHolding, spaceProcessed]);

  const saveTime = async (finalTime) => {
    const newTime = { time: finalTime, date: new Date().toISOString() };
    const updatedTimes = [newTime, ...times].slice(0, 100);
    const updatedAlgTimes = { ...algTimes, [algorithm.name]: { times: updatedTimes } };
    setAlgTimes(updatedAlgTimes);
    await storage.saveAlgTimes(updatedAlgTimes);
    setTime(0);
    generateTrainingScramble();
  };

  const clearHistory = async () => {
    if (confirm(`Clear all practice times for ${algorithm.name}?`)) {
      const updatedAlgTimes = { ...algTimes };
      delete updatedAlgTimes[algorithm.name];
      setAlgTimes(updatedAlgTimes);
      await storage.saveAlgTimes(updatedAlgTimes);
    }
  };

  const formatTimeDisplay = (ms) => {
    if (!ms && ms !== 0) return '-';
    const seconds = Math.floor(ms / 1000);
    const milliseconds = Math.floor((ms % 1000) / 10);
    return `${seconds}.${milliseconds.toString().padStart(2, '0')}`;
  };

  const calculateTrainerStats = () => {
    if (times.length === 0) return { best: null, ao5: null, ao12: null, count: 0 };
    const timeValues = times.map(t => t.time);
    const best = Math.min(...timeValues);

    const calculateAverage = (arr, n) => {
      if (arr.length < n) return null;
      const subset = arr.slice(0, n);
      const sorted = [...subset].sort((a, b) => a - b);
      const trimmed = sorted.slice(1, -1);
      return trimmed.reduce((a, b) => a + b, 0) / trimmed.length;
    };

    return {
      best,
      ao5: calculateAverage(timeValues, 5),
      ao12: calculateAverage(timeValues, 12),
      count: times.length
    };
  };

  const stats = calculateTrainerStats();
  const recentTimes = times.slice(0, 10);

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="training-modal">
        <div className="training-modal-header">
          <h2>⏱️ {algorithm.name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="training-modal-body">
          <div className="training-scramble-section">
            <div className="training-cube-face">
              <CubeFace pattern={algorithm.pattern} size={80} type={category} />
            </div>
            <div className="training-scramble-info">
              <div className="scramble-label">Scramble</div>
              <div className="scramble-moves">{currentScramble}</div>
              <button className="btn btn-secondary btn-sm" onClick={generateTrainingScramble}>
                🔄 New Scramble
              </button>
              <div className="training-solution">
                <div className="scramble-label">Solution {selectedVariant.name && `(${selectedVariant.name})`}</div>
                <div className="alg-solution">{selectedVariant.solution}</div>
              </div>
            </div>
          </div>

          <div className="training-timer-section">
            <div className={`training-timer-display ${isHolding ? 'holding' : ''} ${isRunning ? 'running' : ''}`}>
              {formatTimeDisplay(time)}
            </div>
            <div className="training-timer-hint">
              {isRunning ? 'Press SPACE to stop' : 'Hold SPACE to start'}
            </div>
          </div>

          <div className="training-stats-section">
            <h3>📊 Your Stats</h3>
            <div className="training-stats-grid">
              <div className="training-stat-card">
                <div className="training-stat-label">Best</div>
                <div className="training-stat-value">{stats.best !== null ? formatTimeDisplay(stats.best) + 's' : '-'}</div>
              </div>
              <div className="training-stat-card">
                <div className="training-stat-label">Ao5</div>
                <div className="training-stat-value">{stats.ao5 !== null ? formatTimeDisplay(stats.ao5) + 's' : '-'}</div>
              </div>
              <div className="training-stat-card">
                <div className="training-stat-label">Ao12</div>
                <div className="training-stat-value">{stats.ao12 !== null ? formatTimeDisplay(stats.ao12) + 's' : '-'}</div>
              </div>
            </div>

            {recentTimes.length > 0 && (
              <div className="training-recent-times">
                <h4>Recent Times</h4>
                <div className="recent-times-list">
                  {recentTimes.map((t, idx) => (
                    <span key={idx} className={`recent-time-chip ${t.time === stats.best ? 'best' : ''}`}>
                      {formatTimeDisplay(t.time)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="training-practice-count">Total practices: {stats.count}</div>

            {times.length > 0 && (
              <button className="btn btn-danger btn-sm" onClick={clearHistory}>Clear History</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// AUTH MODAL
// ============================================
function AuthModal({ isOpen, onClose }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, verifyCode } = useAuth();

  if (!isOpen) return null;

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    const result = await signIn(email);
    setIsLoading(false);
    if (result.success) {
      setStep('code');
    } else {
      setError(result.error || 'Failed to send code.');
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    const result = await verifyCode(email, code);
    setIsLoading(false);
    if (result.success) {
      setStep('success');
      setTimeout(() => {
        onClose();
        setStep('email');
        setEmail('');
        setCode('');
      }, 2000);
    } else {
      setError(result.error || 'Invalid code.');
    }
  };

  const handleClose = () => {
    onClose();
    setStep('email');
    setEmail('');
    setCode('');
    setError('');
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="auth-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>×</button>
        
        {step === 'email' && (
          <>
            <h2>🔐 Sign In</h2>
            <p>Sign in to sync your data across devices and compete on the leaderboard!</p>
            {error && <div className="auth-error">{error}</div>}
            <form onSubmit={handleSendCode}>
              <input type="email" className="auth-input" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={isLoading || !email}>
                {isLoading ? 'Sending...' : 'Send Magic Code'}
              </button>
            </form>
          </>
        )}

        {step === 'code' && (
          <>
            <h2>📬 Check Your Email</h2>
            <p>We sent a 6-digit code to <strong>{email}</strong></p>
            {error && <div className="auth-error">{error}</div>}
            <form onSubmit={handleVerifyCode}>
              <input type="text" className="auth-input" placeholder="Enter 6-digit code" value={code} onChange={e => setCode(e.target.value)} maxLength={6} required autoFocus style={{ textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.5rem' }} />
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={isLoading || code.length !== 6}>
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </button>
            </form>
          </>
        )}

        {step === 'success' && (
          <div className="auth-success">
            <h3>✅ You're signed in!</h3>
            <p>Your data will now sync across devices.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// SYNC STATUS
// ============================================
function SyncStatus({ onSignInClick }) {
  const { user, syncStatus, signOut, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return (
      <div className="sync-status">
        <div className="sync-indicator offline"></div>
        <span className="sync-user">Not signed in</span>
        <button className="sync-button" onClick={onSignInClick}>Sign In</button>
      </div>
    );
  }

  return (
    <div className="sync-status">
      <div className={`sync-indicator ${syncStatus}`}></div>
      <span className="sync-user" title={user.email}>{user.email.split('@')[0]}</span>
      <button className="sync-button" onClick={signOut}>Sign Out</button>
    </div>
  );
}

// ============================================
// TIMER PAGE
// ============================================
function Timer() {
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const [scramble, setScramble] = useState(generateScramble());
  const [solves, setSolves] = useState([]);
  const [inspectionEnabled, setInspectionEnabled] = useState(true);
  const [inspectionTime, setInspectionTime] = useState(15);
  const [isInspecting, setIsInspecting] = useState(false);
  const [penalty, setPenalty] = useState(null);
  const [spaceProcessed, setSpaceProcessed] = useState(false);
  
  const intervalRef = useRef(null);
  const inspectionIntervalRef = useRef(null);
  const holdTimerRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      const savedSolves = await storage.loadSolves();
      setSolves(savedSolves);
      const savedInspection = await storage.loadInspectionEnabled();
      setInspectionEnabled(savedInspection);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (solves.length > 0) {
      storage.saveSolves(solves);
    }
  }, [solves]);

  useEffect(() => {
    storage.saveInspectionEnabled(inspectionEnabled);
  }, [inspectionEnabled]);

  useEffect(() => {
    if (isRunning) {
      startTimeRef.current = Date.now();
      intervalRef.current = setInterval(() => {
        setTime(Date.now() - startTimeRef.current);
      }, 10);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning]);

  // Audio cue function
  const playBeep = useCallback((frequency = 800, duration = 100) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration / 1000);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration / 1000);
    } catch (error) {
      console.log('Audio not supported');
    }
  }, []);

  useEffect(() => {
    if (isInspecting) {
      inspectionIntervalRef.current = setInterval(() => {
        setInspectionTime(t => {
          const newTime = t - 0.1;

          // Audio cues at 8 seconds (beep) and 12 seconds (beep)
          if (Math.abs(newTime - 8) < 0.05) {
            playBeep(800, 100); // 8 seconds remaining
          } else if (Math.abs(newTime - 12) < 0.05) {
            playBeep(600, 100); // 12 seconds remaining (lower pitch)
          }

          if (newTime <= -2) {
            setPenalty('dnf');
            stopInspection();
            return newTime;
          }
          if (newTime <= 0 && !penalty) {
            setPenalty('plus2');
          }
          return newTime;
        });
      }, 100);
    } else {
      clearInterval(inspectionIntervalRef.current);
    }
    return () => clearInterval(inspectionIntervalRef.current);
  }, [isInspecting, playBeep]);

  const newScramble = () => {
    setScramble(generateScramble());
    setTime(0);
    setPenalty(null);
  };

  const startInspection = () => {
    setIsInspecting(true);
    setInspectionTime(15);
    setPenalty(null);
  };

  const stopInspection = () => {
    setIsInspecting(false);
    clearInterval(inspectionIntervalRef.current);
  };

  const handleKeyDown = useCallback((e) => {
    if (e.code === 'Space') {
      e.preventDefault();
      if (e.repeat) return;

      if (isInspecting) {
        if (!isHolding) {
          setIsHolding(true);
          setSpaceProcessed(false);
          holdTimerRef.current = setTimeout(() => {
            stopInspection();
            setIsRunning(true);
            setTime(0);
            setIsHolding(false);
            setSpaceProcessed(true);
          }, 300);
        }
        return;
      }

      if (isRunning && !spaceProcessed) {
        setIsRunning(false);
        saveSolve(Date.now() - startTimeRef.current);
        setSpaceProcessed(true);
      } else if (!isHolding && !isRunning) {
        setIsHolding(true);
        setSpaceProcessed(false);
        holdTimerRef.current = setTimeout(() => {
          if (inspectionEnabled) {
            startInspection();
          } else {
            setIsRunning(true);
            setTime(0);
          }
          setIsHolding(false);
          setSpaceProcessed(true);
        }, 300);
      }
    }
  }, [isRunning, isHolding, isInspecting, inspectionEnabled, spaceProcessed]);

  const handleKeyUp = useCallback((e) => {
    if (e.code === 'Space') {
      setSpaceProcessed(false);
      if (isHolding && !isRunning && !isInspecting) {
        clearTimeout(holdTimerRef.current);
        setIsHolding(false);
      }
    }
  }, [isHolding, isRunning, isInspecting]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  const saveSolve = (finalTime) => {
    const solve = {
      id: Date.now(),
      time: finalTime,
      scramble: scramble,
      date: new Date(),
      penalty: penalty
    };
    setSolves(prev => [solve, ...prev]);
    newScramble();
  };

  const deleteSolve = (id) => {
    setSolves(prev => prev.filter(s => s.id !== id));
  };

  const toggleDNF = (id) => {
    setSolves(prev => prev.map(s => s.id === id ? { ...s, penalty: s.penalty === 'dnf' ? null : 'dnf' } : s));
  };

  const togglePlusTwo = (id) => {
    setSolves(prev => prev.map(s => s.id === id ? { ...s, penalty: s.penalty === 'plus2' ? null : 'plus2' } : s));
  };

  const getDisplayTime = (solve) => {
    if (solve.penalty === 'dnf') return 'DNF';
    const displayTime = solve.penalty === 'plus2' ? solve.time + 2000 : solve.time;
    return formatTime(displayTime) + (solve.penalty === 'plus2' ? '+' : '');
  };

  const stats = calculateStats(solves.map(s => ({ ...s, dnf: s.penalty === 'dnf', time: s.penalty === 'plus2' ? s.time + 2000 : s.time })));

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Timer</h1>
        <p className="page-subtitle">Practice with official WCA inspection rules</p>
      </div>

      <div className="timer-container">
        <div className="scramble-section">
          <div className="scramble-display">{scramble}</div>
          <CubeVisualization scramble={scramble} />
          <button className="btn btn-primary" onClick={newScramble}>New Scramble</button>
        </div>

        <div className="toggle-container">
          <span className="toggle-label">WCA Inspection (15s)</span>
          <div className={`toggle-switch ${inspectionEnabled ? 'active' : ''}`} onClick={() => setInspectionEnabled(!inspectionEnabled)}>
            <div className="toggle-slider"></div>
          </div>
        </div>

        {isInspecting && (
          <div className="inspection-container">
            <div className="inspection-label">INSPECTION</div>
            <div className={`inspection-timer ${inspectionTime <= 3 ? 'critical' : inspectionTime <= 8 ? 'warning' : ''}`}>
              {Math.max(0, Math.ceil(inspectionTime))}
            </div>
          </div>
        )}

        <div className={`timer-display ${isRunning ? 'running' : ''} ${isHolding ? 'holding' : ''}`}>
          {formatTime(time)}
        </div>

        <div className="timer-hint">
          {isInspecting ? 'Hold SPACE to start solve' : isRunning ? 'Press SPACE to stop' : isHolding ? 'Release to cancel, keep holding to start' : 'Hold SPACE to start'}
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Session Mean</div>
          <div className="stat-value">{stats.mean ? formatTime(stats.mean) : '-'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Ao5</div>
          <div className="stat-value">{stats.ao5 ? formatTime(stats.ao5) : '-'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Ao12</div>
          <div className="stat-value">{stats.ao12 ? formatTime(stats.ao12) : '-'}</div>
        </div>
        <div className="stat-card highlight">
          <div className="stat-label">PB</div>
          <div className="stat-value">{stats.pb ? formatTime(stats.pb) : '-'}</div>
        </div>
      </div>

      {solves.length > 0 && (
        <div className="recent-solves">
          <h3>Recent Solves ({solves.length})</h3>
          <div className="solves-list">
            {solves.slice(0, 12).map((solve, idx) => (
              <div key={solve.id} className={`solve-item ${solve.penalty === 'dnf' ? 'dnf' : ''}`}>
                <span className="solve-num">#{solves.length - idx}</span>
                <span className="solve-time">{getDisplayTime(solve)}</span>
                <div className="solve-actions">
                  <button onClick={() => togglePlusTwo(solve.id)} className={solve.penalty === 'plus2' ? 'active' : ''}>+2</button>
                  <button onClick={() => toggleDNF(solve.id)} className={solve.penalty === 'dnf' ? 'active' : ''}>DNF</button>
                  <button onClick={() => deleteSolve(solve.id)}>🗑️</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================
// ALGORITHMS PAGE (with Practice buttons)
// ============================================
function AlgorithmCard({ alg, category, isLearned, onToggleLearned, onPractice, getBestTime, algTimes }) {
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [selectedAlgId, setSelectedAlgId] = useState('standard');
  
  // Get algorithms array (new format) or create from old format
  const algVariants = alg.algorithms || [{
    id: 'standard',
    name: 'Standard',
    solution: alg.solution || alg.moves,
    scramble: alg.scramble,
    moveCount: (alg.solution || alg.moves || '').split(' ').filter(m => m).length,
    notes: alg.description || ''
  }];
  
  const primaryAlg = algVariants[0];
  const alternatives = algVariants.slice(1);
  const selectedVariant = algVariants.find(a => a.id === selectedAlgId) || primaryAlg;
  
  const handlePractice = () => {
    onPractice({
      ...alg,
      // Pass the selected variant's data
      selectedVariant: selectedVariant,
      scramble: selectedVariant.scramble,
      solution: selectedVariant.solution
    });
  };

  return (
    <div className={`alg-card ${isLearned ? 'learned' : ''}`}>
      <div className="alg-header" onClick={() => onToggleLearned(alg.name)}>
        <h4>{alg.name}</h4>
        <div className="checkbox">{isLearned && '✓'}</div>
      </div>
      
      <div className="alg-case">{alg.case}</div>
      
      <div className="alg-cube-section">
        <div className="alg-cube-face">
          <CubeFace pattern={alg.pattern} size={70} type={category} />
        </div>
        <div className="alg-scramble">
          <div className="scramble-label">Setup</div>
          <div className="scramble-text">{selectedVariant.scramble}</div>
        </div>
      </div>

      <div className="alg-solution-section">
        <div className="alg-solution">{selectedVariant.solution}</div>
        <div className="alg-solution-meta">
          <span className="move-count">{selectedVariant.moveCount} moves</span>
          {selectedVariant.notes && <span className="alg-notes">{selectedVariant.notes}</span>}
        </div>
      </div>
      
      {/* Alternatives Section */}
      {alternatives.length > 0 && (
        <div className="alg-alternatives">
          <button 
            className="alternatives-toggle"
            onClick={() => setShowAlternatives(!showAlternatives)}
          >
            <span>{showAlternatives ? '▼' : '▶'}</span>
            <span>Show {alternatives.length} alternative{alternatives.length > 1 ? 's' : ''}</span>
          </button>
          
          {showAlternatives && (
            <div className="alternatives-list">
              {/* Primary algorithm option */}
              <div 
                className={`alternative-item ${selectedAlgId === primaryAlg.id ? 'selected' : ''}`}
                onClick={() => setSelectedAlgId(primaryAlg.id)}
              >
                <div className="alt-header">
                  <span className="alt-name">{primaryAlg.name}</span>
                  <span className="alt-moves">{primaryAlg.moveCount} moves</span>
                  {selectedAlgId === primaryAlg.id && <span className="alt-selected">✓</span>}
                </div>
                <div className="alt-solution">{primaryAlg.solution}</div>
                {primaryAlg.notes && <div className="alt-notes">{primaryAlg.notes}</div>}
              </div>
              
              {/* Alternative algorithms */}
              {alternatives.map((alt) => (
                <div 
                  key={alt.id}
                  className={`alternative-item ${selectedAlgId === alt.id ? 'selected' : ''}`}
                  onClick={() => setSelectedAlgId(alt.id)}
                >
                  <div className="alt-header">
                    <span className="alt-name">{alt.name}</span>
                    <span className="alt-moves">{alt.moveCount} moves</span>
                    {selectedAlgId === alt.id && <span className="alt-selected">✓</span>}
                  </div>
                  <div className="alt-solution">{alt.solution}</div>
                  {alt.notes && <div className="alt-notes">{alt.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      <div className="alg-badges">
        <span className={`difficulty-badge ${alg.difficulty}`}>{alg.difficulty}</span>
        {alg.frequency && (
          <span className={`frequency-badge ${alg.frequency}`}>
            {alg.frequency === 'very-common' ? '⭐⭐⭐' : alg.frequency === 'common' ? '⭐⭐' : '⭐'}
          </span>
        )}
      </div>

      <div className="alg-card-footer">
        <button className="btn btn-practice" onClick={handlePractice}>
          ⏱️ Practice
        </button>
        {getBestTime(alg.name) && (
          <span className="alg-best-time">Best: {formatTime(getBestTime(alg.name))}</span>
        )}
      </div>
    </div>
  );
}

function AlgorithmsPage() {
  const [selectedCategory, setSelectedCategory] = useState('oll');
  const [learnedAlgs, setLearnedAlgs] = useState({});
  const [selectedCase, setSelectedCase] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [trainingAlg, setTrainingAlg] = useState(null);
  const [algTimes, setAlgTimes] = useState({});

  useEffect(() => {
    const loadData = async () => {
      const savedAlgs = await storage.loadLearnedAlgs();
      setLearnedAlgs(savedAlgs);
      const savedTimes = await storage.loadAlgTimes();
      setAlgTimes(savedTimes);
    };
    loadData();
  }, []);

  useEffect(() => {
    storage.saveLearnedAlgs(learnedAlgs);
  }, [learnedAlgs]);

  const toggleLearned = (algName) => {
    setLearnedAlgs(prev => ({ ...prev, [algName]: !prev[algName] }));
  };

  const getBestTime = (algName) => {
    const data = algTimes[algName];
    if (!data || !data.times || data.times.length === 0) return null;
    return Math.min(...data.times.map(t => t.time));
  };

  const algs = algorithms[selectedCategory] || [];
  
  const getCaseTypes = () => {
    const cases = new Set(['all']);
    algs.forEach(alg => cases.add(alg.case));
    return Array.from(cases);
  };
  const caseTypes = getCaseTypes();

  const getFilteredAlgs = () => {
    let filtered = selectedCase === 'all' ? algs : algs.filter(a => a.case === selectedCase);
    
    if (sortBy === 'frequency') {
      const freqOrder = { 'very-common': 0, 'common': 1, 'rare': 2 };
      filtered = [...filtered].sort((a, b) => freqOrder[a.frequency] - freqOrder[b.frequency]);
    } else if (sortBy === 'learned') {
      filtered = [...filtered].sort((a, b) => {
        const aLearned = learnedAlgs[a.name] ? 1 : 0;
        const bLearned = learnedAlgs[b.name] ? 1 : 0;
        return aLearned - bLearned;
      });
    }
    return filtered;
  };

  const filteredAlgs = getFilteredAlgs();
  const learnedCount = filteredAlgs.filter(a => learnedAlgs[a.name]).length;
  const progress = filteredAlgs.length > 0 ? Math.round((learnedCount / filteredAlgs.length) * 100) : 0;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Algorithms</h1>
        <p className="page-subtitle">Master all essential algorithms for speedsolving</p>
      </div>

      <NotationGuide />

      <div className="progress-banner">
        <div className="progress-info">
          <h3>{selectedCategory.toUpperCase()} Progress</h3>
          <p>{learnedCount} of {filteredAlgs.length} learned ({progress}%)</p>
        </div>
        <div className="progress-bar">
          <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
        </div>
      </div>

      <div className="filter-group">
        {['oll', 'pll', 'f2l'].map(cat => (
          <div 
            key={cat}
            className={`filter-chip ${selectedCategory === cat ? 'active' : ''}`}
            onClick={() => { setSelectedCategory(cat); setSelectedCase('all'); }}
          >
            {cat.toUpperCase()} ({algorithms[cat]?.length || 0})
          </div>
        ))}
      </div>

      <div className="filter-group" style={{ marginTop: '1rem' }}>
        {caseTypes.map(caseType => (
          <div
            key={caseType}
            className={`filter-chip ${selectedCase === caseType ? 'active' : ''}`}
            onClick={() => setSelectedCase(caseType)}
          >
            {caseType === 'all' ? 'All Cases' : caseType}
          </div>
        ))}
      </div>

      <div className="sort-controls">
        <span className="sort-label">Sort by:</span>
        <div className="filter-group" style={{ display: 'inline-flex', marginTop: 0 }}>
          <div className={`filter-chip ${sortBy === 'default' ? 'active' : ''}`} onClick={() => setSortBy('default')}>Default</div>
          <div className={`filter-chip ${sortBy === 'frequency' ? 'active' : ''}`} onClick={() => setSortBy('frequency')}>⭐ Most Common</div>
          <div className={`filter-chip ${sortBy === 'learned' ? 'active' : ''}`} onClick={() => setSortBy('learned')}>📚 Unlearned First</div>
        </div>
      </div>

      <div className="alg-grid">
        {filteredAlgs.map((alg, idx) => (
          <AlgorithmCard
            key={idx}
            alg={alg}
            category={selectedCategory}
            isLearned={learnedAlgs[alg.name]}
            onToggleLearned={toggleLearned}
            onPractice={setTrainingAlg}
            getBestTime={getBestTime}
            algTimes={algTimes}
          />
        ))}
      </div>

      {trainingAlg && (
        <AlgorithmTrainer
          algorithm={trainingAlg}
          category={selectedCategory}
          onClose={() => setTrainingAlg(null)}
          algTimes={algTimes}
          setAlgTimes={setAlgTimes}
        />
      )}
    </div>
  );
}

// ============================================
// PRACTICE PAGE (Training Plans)
// ============================================
function PracticePage({ userLevel, setUserLevel }) {
  const [expandedDrill, setExpandedDrill] = useState(null);
  const [completedDrills, setCompletedDrills] = useState({});
  const [practiceTimes, setPracticeTimes] = useState({ cross: [], f2l: [], oll: [], pll: [] });

  useEffect(() => {
    const loadData = async () => {
      const saved = await storage.loadCompletedDrills();
      setCompletedDrills(saved);
      const savedPracticeTimes = await storage.loadPracticeTimes();
      setPracticeTimes(savedPracticeTimes);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (Object.keys(completedDrills).length > 0) {
      storage.saveCompletedDrills(completedDrills);
    }
  }, [completedDrills]);

  useEffect(() => {
    storage.savePracticeTimes(practiceTimes);
  }, [practiceTimes]);

  const practicePlans = {
    beginner: {
      title: 'Beginner Training Plan',
      description: 'Build a solid foundation with these essential drills',
      drills: [
        {
          id: 'beginner-cross',
          name: 'Cross Practice',
          duration: '10 min',
          icon: '✚',
          overview: 'Master cross solving with proper planning',
          goals: 'Develop the ability to plan and execute the cross efficiently. The cross is your foundation - a good cross sets up easier F2L and faster overall times. This drill builds planning skills and move economy.',
          timing: 'Start this as soon as you can solve the cube consistently. This is fundamental for all skill levels. Beginners should spend 10-15 minutes daily on cross practice.',
          prerequisites: 'Ability to solve a Rubik\'s Cube using any method. Understanding of basic cube notation (R, U, D, F, B, L).',
          exercises: [
            { title: 'Cross-Only Solves', description: 'Solve only the cross 20 times', steps: ['Scramble the cube', 'Plan the entire cross during inspection', 'Execute without looking', 'Aim for 8 moves or less'] },
            { title: 'Color Neutrality', description: 'Practice different starting colors', steps: ['5 solves on white', '5 solves on yellow', 'Compare times'] }
          ],
          tips: ['Plan the entire cross during inspection', 'Look for pieces that solve together', 'Avoid rotations']
        },
        {
          id: 'beginner-f2l',
          name: 'Basic F2L Cases',
          duration: '15 min',
          icon: '🔲',
          overview: 'Learn intuitive F2L pairing',
          goals: 'Build intuitive understanding of how corners and edges pair together. F2L (First Two Layers) is the most time-consuming step, so efficiency here dramatically improves your overall times.',
          timing: 'Begin once you\'re comfortable with cross solving. Spend 2-3 weeks on basic intuitive F2L before worrying about all 41 cases.',
          prerequisites: 'Consistent cross solving in under 10 seconds. Understanding of how corner-edge pairs form the first two layers.',
          exercises: [
            { title: 'Corner-Edge Pairing', description: 'Practice pairing in top layer', steps: ['Find corner and edge', 'Bring both to top', 'Position together', 'Insert with R U R\''] },
            { title: 'Easy Cases', description: 'Master the 4 easiest cases', steps: ['Learn U R U\' R\'', 'Learn F\' U\' F', 'Practice 10 times each'] }
          ],
          tips: ['Trust your intuition', 'Keep corner and edge together', 'Practice slowly']
        },
        {
          id: 'beginner-ll',
          name: 'Last Layer Basics',
          duration: '10 min',
          icon: '🎯',
          overview: 'Beginner last layer method',
          goals: 'Master the foundational last layer algorithms before learning full OLL/PLL. This 4-algorithm approach gets you solving consistently while you build F2L skills.',
          timing: 'Practice this alongside basic F2L. Once you can complete full solves in under 90 seconds consistently, consider learning 2-look OLL/PLL.',
          prerequisites: 'Solid cross and basic F2L understanding. Ability to execute simple algorithms without mistakes.',
          exercises: [
            { title: 'Beginner OLL', description: 'Orient using basic algs', steps: ['Make yellow cross (F R U R\' U\' F\')', 'Orient corners (R U R\' U R U2 R\')', 'Practice each 10 times'] },
            { title: 'Beginner PLL', description: 'Permute corners then edges', steps: ['Learn corner swap', 'Learn edge cycle', 'Do 10 full LL solves'] }
          ],
          tips: ['Orientation before permutation', 'Look for headlights', 'Accuracy over speed']
        }
      ]
    },
    intermediate: {
      title: 'Intermediate Training Plan',
      description: 'Advance your skills with CFOP fundamentals',
      drills: [
        {
          id: 'inter-cross',
          name: 'Cross Optimization',
          duration: '10 min',
          icon: '⚡',
          overview: 'Reduce cross to under 8 moves',
          goals: 'Improve cross efficiency and develop advanced planning skills. Optimized cross solutions save time and often preserve F2L pairs. Blind cross execution builds confidence and allows for earlier lookahead.',
          timing: 'Begin when averaging under 45 seconds. This is a continuous improvement skill - even sub-15 solvers practice cross optimization daily.',
          prerequisites: 'Consistent cross solving. Ability to plan basic crosses during inspection. Comfortable with cube rotations and D-layer moves.',
          exercises: [
            { title: 'Move Count Challenge', description: 'Solve cross in 8 moves or less', steps: ['Plan during inspection', 'Count moves', 'Find better solution if over 8'] },
            { title: 'Blind Cross', description: 'Execute without looking', steps: ['15 second inspection', 'Plan all 4 pieces', 'Close eyes and execute'] }
          ],
          tips: ['Look for pairs', 'Use D-layer moves', 'Minimize rotations']
        },
        {
          id: 'inter-f2l',
          name: 'Advanced F2L',
          duration: '15 min',
          icon: '🔷',
          overview: 'Learn all 41 F2L cases',
          goals: 'Master all F2L cases and develop lookahead to eliminate pauses. F2L efficiency is the biggest factor in reaching sub-20 times. Smooth F2L with lookahead is more important than fast execution.',
          timing: 'Start learning when averaging sub-45 seconds. Expect 2-3 months to learn all cases comfortably. Lookahead development is ongoing.',
          prerequisites: 'Solid intuitive F2L understanding. Can solve all basic cases without thinking. Averaging under 45 seconds.',
          exercises: [
            { title: 'Case Recognition', description: 'Identify cases instantly', steps: ['Review all cases', 'Focus on corner orientation', 'Then edge orientation', 'Under 1 second recognition'] },
            { title: 'Lookahead Basics', description: 'Track the next pair', steps: ['Solve at 50% speed', 'Find next pair while solving', 'No pauses between pairs'] }
          ],
          tips: ['Slow with lookahead > fast with pauses', 'Use peripheral vision', 'Practice difficult cases separately']
        },
        {
          id: 'inter-oll',
          name: 'Two-Look OLL',
          duration: '10 min',
          icon: '👁️',
          overview: 'Master 10 algorithms for 2-look OLL',
          goals: 'Replace beginner last layer with efficient 2-look OLL. This reduces last layer time significantly while requiring only 10 algorithms instead of 57. Essential stepping stone to full OLL.',
          timing: 'Learn when averaging sub-40 seconds and comfortable with F2L. Most cubers use 2-look OLL until they reach sub-20 seconds.',
          prerequisites: 'Solid F2L skills (can complete F2L in under 20 seconds). Ability to execute algorithms smoothly. Understanding of edge vs corner orientation.',
          exercises: [
            { title: 'Edge Orientation', description: 'Learn 3 EO cases', steps: ['Line: F R U R\' U\' F\'', 'L-shape: F U R U\' R\' F\'', 'Dot case', '20 random drills'] },
            { title: 'Corner Orientation', description: 'Learn 7 CO cases', steps: ['Sune and Antisune', 'Both Pi cases', 'Headlights and chameleon'] }
          ],
          tips: ['Edges first, then corners', 'Learn mirrors together']
        },
        {
          id: 'inter-pll',
          name: 'Two-Look PLL',
          duration: '10 min',
          icon: '🔄',
          overview: 'Master 6 algorithms for 2-look PLL',
          goals: 'Complete the 2-look last layer system with efficient PLL algorithms. Combined with 2-look OLL, this gets you solving the last layer in 2-4 seconds consistently.',
          timing: 'Learn alongside or right after 2-look OLL. Essential for breaking the sub-30 barrier. Use until you\'re ready to invest in learning full PLL (21 algorithms).',
          prerequisites: 'Comfortable with 2-look OLL or beginner method. Can execute algorithms at moderate speed. Understanding of piece permutation vs orientation.',
          exercises: [
            { title: 'Corner Permutation', description: 'Learn corner swaps', steps: ['Aa perm', 'E perm', 'Practice recognition'] },
            { title: 'Edge Permutation', description: 'Learn edge cycles', steps: ['Ua and Ub perms', 'H perm', 'Z perm'] }
          ],
          tips: ['Look for headlights', 'Recognition is key']
        }
      ]
    },
    advanced: {
      title: 'Advanced Training Plan',
      description: 'Push your limits with elite techniques',
      drills: [
        {
          id: 'adv-xcross',
          name: 'X-Cross',
          duration: '15 min',
          icon: '✖️',
          overview: 'Solve cross + first F2L pair together',
          goals: 'Master extended cross techniques to solve the first F2L pair during cross. X-Cross can save 3-5 seconds per solve when executed well, but requires advanced planning and cube state awareness.',
          timing: 'Start practicing when averaging sub-20 seconds with solid lookahead. X-Cross is an advanced skill - expect 3-6 months of practice to spot and execute consistently.',
          prerequisites: 'Sub-20 average. Can plan optimal cross during inspection. Strong F2L case recognition. Ability to track pieces during cross execution.',
          exercises: [
            { title: 'Spot X-Cross', description: 'Recognize opportunities', steps: ['Generate scrambles', 'Look for easy X-cross setups', 'Plan during inspection'] },
            { title: 'Execute X-Cross', description: 'Practice common patterns', steps: ['Edge + corner adjacent', 'Keyhole techniques', 'Preserve pairs while solving cross'] }
          ],
          tips: ['Don\'t force X-cross', 'Only when it\'s clearly better', 'Plan completely']
        },
        {
          id: 'adv-lookahead',
          name: 'Lookahead Mastery',
          duration: '15 min',
          icon: '👀',
          overview: 'Eliminate all pauses in F2L',
          goals: 'Develop elite lookahead skills to maintain constant turning speed throughout F2L. Perfect lookahead is the difference between sub-15 and sub-10 times. This is the hardest skill to master but yields the biggest improvements.',
          timing: 'Begin when averaging sub-18 seconds with all F2L cases learned. Lookahead is a lifelong skill - even world-class solvers practice this. Expect 6-12 months to see major improvement.',
          prerequisites: 'All 41 F2L cases learned and automatic. Sub-18 average. Strong muscle memory for F2L execution. Ability to recognize cases from any angle.',
          exercises: [
            { title: 'Metronome Practice', description: 'Constant turning speed', steps: ['Set slow metronome', 'Turn once per beat', 'No pauses allowed', 'Gradually increase'] },
            { title: 'Tracking Drill', description: 'Always know next pair', steps: ['After each insert, say next pair location', 'Practice until automatic'] }
          ],
          tips: ['Your eyes should always be ahead', 'Smooth > fast', 'Trust muscle memory']
        },
        {
          id: 'adv-oll',
          name: 'Full OLL',
          duration: '15 min',
          icon: '⭐',
          overview: 'Learn all 57 OLL cases',
          goals: 'Complete your OLL knowledge to solve the last layer top in one look. Full OLL saves 1-2 seconds per solve and is essential for sub-15 times. Learning all 57 cases also dramatically improves pattern recognition skills.',
          timing: 'Start when averaging sub-20 seconds comfortably. Learn 2-3 cases per week for sustainable progress. Most cubers take 4-6 months to learn full OLL. Don\'t rush - bad habits are hard to fix.',
          prerequisites: 'Solid 2-look OLL (know it backwards and forwards). Sub-20 average. Strong F2L with some lookahead. Finger trick fundamentals mastered.',
          exercises: [
            { title: 'Learn by Groups', description: 'Master one group at a time', steps: ['Dots, Lines, Crosses', 'P, W, L shapes', 'Fish, Knight, Awkward'] },
            { title: 'Speed Drills', description: 'Sub-1 second execution', steps: ['Practice each alg 20 times', 'Optimize finger tricks', 'Time each execution'] }
          ],
          tips: ['Learn most common first', 'Focus on recognition', 'Consistent finger tricks']
        },
        {
          id: 'adv-pll',
          name: 'Full PLL',
          duration: '15 min',
          icon: '🏆',
          overview: 'Master all 21 PLL cases',
          goals: 'Master one-look PLL to complete the last layer efficiently every time. Full PLL is essential for sub-20 times and relatively easy to learn (only 21 cases). Fast, accurate PLL execution is critical for competitive solving.',
          timing: 'Learn when averaging sub-25 seconds. Can be learned alongside or before full OLL. Most cubers learn full PLL in 2-3 months. This is typically learned before full OLL due to fewer cases.',
          prerequisites: 'Comfortable with 2-look PLL. Sub-25 average. Good algorithm execution speed. Understanding of 2-sided recognition patterns.',
          exercises: [
            { title: 'Recognition Drill', description: 'Instant case ID', steps: ['2-side recognition', 'Practice with scrambles', 'Under 0.5s recognition'] },
            { title: 'Execution Speed', description: 'Sub-1s all PLLs', steps: ['Optimal finger tricks', 'Drill weak PLLs', 'Time attacks'] }
          ],
          tips: ['AUF prediction', 'Learn multiple angles', 'Finger trick optimization']
        }
      ]
    }
  };

  const currentPlan = practicePlans[userLevel];
  const completedCount = currentPlan.drills.filter(d => completedDrills[d.id]).length;

  const toggleDrillComplete = (drillId) => {
    setCompletedDrills(prev => ({ ...prev, [drillId]: !prev[drillId] }));
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Practice Plans</h1>
        <p className="page-subtitle">Structured training to improve your times</p>
      </div>

      <div className="level-selector">
        <span className="level-label">Your Level:</span>
        <div className="filter-group">
          {['beginner', 'intermediate', 'advanced'].map(level => (
            <div
              key={level}
              className={`filter-chip ${userLevel === level ? 'active' : ''}`}
              onClick={() => setUserLevel(level)}
            >
              {level.charAt(0).toUpperCase() + level.slice(1)}
            </div>
          ))}
        </div>
      </div>

      {/* Cross Trainer */}
      <CrossTrainer
        practiceTimes={practiceTimes}
        setPracticeTimes={setPracticeTimes}
      />

      <div className="practice-plan">
        <div className="plan-header">
          <h2>{currentPlan.title}</h2>
          <p>{currentPlan.description}</p>
          <div className="plan-progress">
            <span>{completedCount} of {currentPlan.drills.length} drills completed</span>
            <div className="progress-bar">
              <div className="progress-bar-fill" style={{ width: `${(completedCount / currentPlan.drills.length) * 100}%` }}></div>
            </div>
          </div>
        </div>

        <div className="drills-list">
          {currentPlan.drills.map((drill) => (
            <div key={drill.id} className={`drill-card ${completedDrills[drill.id] ? 'completed' : ''}`}>
              <div className="drill-header" onClick={() => setExpandedDrill(expandedDrill === drill.id ? null : drill.id)}>
                <div className="drill-icon">{drill.icon}</div>
                <div className="drill-info">
                  <h3>{drill.name}</h3>
                  <span className="drill-duration">{drill.duration}</span>
                </div>
                <div className="drill-checkbox" onClick={(e) => { e.stopPropagation(); toggleDrillComplete(drill.id); }}>
                  {completedDrills[drill.id] ? '✓' : ''}
                </div>
              </div>

              {expandedDrill === drill.id && (
                <div className="drill-content">
                  <p className="drill-overview">{drill.overview}</p>

                  {/* Context Sections */}
                  {drill.goals && (
                    <div className="drill-context-section">
                      <h4>🎯 Why This Drill?</h4>
                      <p>{drill.goals}</p>
                    </div>
                  )}

                  {drill.timing && (
                    <div className="drill-context-section">
                      <h4>⏰ When to Practice</h4>
                      <p>{drill.timing}</p>
                    </div>
                  )}

                  {drill.prerequisites && (
                    <div className="drill-context-section">
                      <h4>✅ Prerequisites</h4>
                      <p>{drill.prerequisites}</p>
                    </div>
                  )}

                  <div className="drill-exercises">
                    {drill.exercises.map((ex, idx) => (
                      <div key={idx} className="exercise">
                        <h4>{ex.title}</h4>
                        <p>{ex.description}</p>
                        <ol>
                          {ex.steps.map((step, sIdx) => (
                            <li key={sIdx}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>

                  <div className="drill-tips">
                    <h4>💡 Tips</h4>
                    <ul>
                      {drill.tips.map((tip, idx) => (
                        <li key={idx}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================
// PROGRESS PAGE
// ============================================
function ProgressPage() {
  const [solves, setSolves] = useState([]);
  const [learnedAlgs, setLearnedAlgs] = useState({});
  const [algTimes, setAlgTimes] = useState({});
  const [timeRange, setTimeRange] = useState('all');

  useEffect(() => {
    const loadData = async () => {
      const savedSolves = await storage.loadSolves();
      setSolves(savedSolves);
      const savedAlgs = await storage.loadLearnedAlgs();
      setLearnedAlgs(savedAlgs);
      const savedTimes = await storage.loadAlgTimes();
      setAlgTimes(savedTimes);
    };
    loadData();
  }, []);

  const filterSolvesByTime = (solves) => {
    if (timeRange === 'all') return solves;
    const now = new Date();
    const cutoff = new Date();
    if (timeRange === 'week') cutoff.setDate(now.getDate() - 7);
    if (timeRange === 'month') cutoff.setMonth(now.getMonth() - 1);
    return solves.filter(s => new Date(s.date) >= cutoff);
  };

  const filteredSolves = filterSolvesByTime(solves);
  const stats = calculateStats(filteredSolves.filter(s => s.penalty !== 'dnf').map(s => ({
    ...s,
    time: s.penalty === 'plus2' ? s.time + 2000 : s.time,
    dnf: false
  })));
  const bestStats = calculateBestAverages(solves.filter(s => s.penalty !== 'dnf').map(s => ({
    ...s,
    time: s.penalty === 'plus2' ? s.time + 2000 : s.time,
    dnf: false
  })));

  const ollLearned = Object.keys(learnedAlgs).filter(k => k.startsWith('OLL') && learnedAlgs[k]).length;
  const pllLearned = Object.keys(learnedAlgs).filter(k => k.includes('Perm') && learnedAlgs[k]).length;
  const f2lLearned = Object.keys(learnedAlgs).filter(k => k.startsWith('Case') && learnedAlgs[k]).length;

  // Get algorithm practice stats
  const algPracticeCount = Object.keys(algTimes).length;
  const totalPractices = Object.values(algTimes).reduce((sum, alg) => sum + (alg.times?.length || 0), 0);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Progress</h1>
        <p className="page-subtitle">Track your speedcubing journey</p>
      </div>

      <div className="filter-group" style={{ marginBottom: '1.5rem' }}>
        {['week', 'month', 'all'].map(range => (
          <div
            key={range}
            className={`filter-chip ${timeRange === range ? 'active' : ''}`}
            onClick={() => setTimeRange(range)}
          >
            {range === 'week' ? 'This Week' : range === 'month' ? 'This Month' : 'All Time'}
          </div>
        ))}
      </div>

      <div className="stats-grid">
        <div className="stat-card highlight">
          <div className="stat-label">Personal Best</div>
          <div className="stat-value">{bestStats.pb ? formatTime(bestStats.pb) : '-'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Best Ao5</div>
          <div className="stat-value">{bestStats.bestAo5 ? formatTime(bestStats.bestAo5) : '-'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Best Ao12</div>
          <div className="stat-value">{bestStats.bestAo12 ? formatTime(bestStats.bestAo12) : '-'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Solves</div>
          <div className="stat-value">{solves.length}</div>
        </div>
      </div>

      <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>📚 Algorithm Progress</h3>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">OLL Learned</div>
          <div className="stat-value">{ollLearned}/57</div>
          <div className="progress-bar" style={{ marginTop: '0.5rem' }}>
            <div className="progress-bar-fill" style={{ width: `${(ollLearned / 57) * 100}%` }}></div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">PLL Learned</div>
          <div className="stat-value">{pllLearned}/21</div>
          <div className="progress-bar" style={{ marginTop: '0.5rem' }}>
            <div className="progress-bar-fill" style={{ width: `${(pllLearned / 21) * 100}%` }}></div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">F2L Cases</div>
          <div className="stat-value">{f2lLearned}/41</div>
          <div className="progress-bar" style={{ marginTop: '0.5rem' }}>
            <div className="progress-bar-fill" style={{ width: `${(f2lLearned / 41) * 100}%` }}></div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Algs Practiced</div>
          <div className="stat-value">{algPracticeCount}</div>
          <div className="stat-subtitle">{totalPractices} total reps</div>
        </div>
      </div>

      {filteredSolves.length > 0 && (
        <>
          <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>📈 {timeRange === 'all' ? 'All Time' : timeRange === 'week' ? 'This Week' : 'This Month'} Stats</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Average</div>
              <div className="stat-value">{stats.mean ? formatTime(stats.mean) : '-'}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Current Ao5</div>
              <div className="stat-value">{stats.ao5 ? formatTime(stats.ao5) : '-'}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Current Ao12</div>
              <div className="stat-value">{stats.ao12 ? formatTime(stats.ao12) : '-'}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Solves</div>
              <div className="stat-value">{filteredSolves.length}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
// ============================================
// DIAGNOSIS PAGE
// ============================================
function DiagnosisPage() {
  const [diagnosisResults, setDiagnosisResults] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [solveCount, setSolveCount] = useState(0);

  useEffect(() => {
    const loadCount = async () => {
      const solves = await storage.loadSolves();
      setSolveCount(solves.length);
    };
    loadCount();
  }, []);

  const runDiagnosis = async () => {
    setIsAnalyzing(true);
    const solves = await storage.loadSolves();

    if (solves.length < 5) {
      alert('You need at least 5 solves to run a meaningful diagnosis. Complete more solves on the Timer page!');
      setIsAnalyzing(false);
      return;
    }

    setTimeout(() => {
      const analysis = analyzeSolves(solves);
      setDiagnosisResults(analysis);
      setIsAnalyzing(false);
    }, 1500);
  };

  const analyzeSolves = (solves) => {
    const validSolves = solves.filter(s => !s.penalty || s.penalty === 'none');
    if (validSolves.length === 0) return getDefaultAnalysis();

    const recentSolves = validSolves.slice(0, Math.min(50, validSolves.length));
    const times = recentSolves.map(s => s.time);

    const avgTime = times.reduce((a, b) => a + b, 0) / times.length;
    const bestTime = Math.min(...times);
    const variance = times.reduce((sum, time) => sum + Math.pow(time - avgTime, 2), 0) / times.length;
    const stdDev = Math.sqrt(variance);
    const consistency = Math.max(0, 100 - (stdDev / avgTime) * 100);

    const crossScore = Math.min(100, Math.max(0, 100 - (avgTime / 1000 - 10) * 2));
    const f2lScore = Math.min(100, Math.max(0, 100 - (avgTime / 1000 - 15) * 1.5));
    const ollScore = Math.min(100, consistency * 0.8 + 20);
    const pllScore = Math.min(100, consistency * 0.9 + 10);
    const lookaheadScore = consistency;

    const recommendations = generateRecommendations({ avgTime, consistency, crossScore, f2lScore, ollScore, pllScore, lookaheadScore });

    return {
      cross: crossScore,
      f2l: f2lScore,
      oll: ollScore,
      pll: pllScore,
      lookahead: lookaheadScore,
      recommendations,
      stats: {
        avgTime: (avgTime / 1000).toFixed(2),
        bestTime: (bestTime / 1000).toFixed(2),
        consistency: consistency.toFixed(0),
        solvesAnalyzed: recentSolves.length
      }
    };
  };

  const generateRecommendations = (scores) => {
    const recommendations = [];
    const { avgTime, consistency, crossScore, f2lScore, ollScore, pllScore, lookaheadScore } = scores;

    const weaknesses = [
      { name: 'Cross', score: crossScore, key: 'cross' },
      { name: 'F2L', score: f2lScore, key: 'f2l' },
      { name: 'OLL', score: ollScore, key: 'oll' },
      { name: 'PLL', score: pllScore, key: 'pll' },
      { name: 'Lookahead', score: lookaheadScore, key: 'lookahead' }
    ].sort((a, b) => a.score - b.score);

    const advice = {
      cross: { title: 'Improve Cross Efficiency', description: 'Focus on planning during inspection. Aim for 8 moves or less. Use the Cross Practice drill.' },
      f2l: { title: 'Speed Up F2L', description: 'Work on case recognition and reduce pauses. Try slow, deliberate practice to build muscle memory.' },
      oll: { title: 'Master OLL Recognition', description: 'Focus on the most common cases first (21, 22, 26, 27). Practice recognition without executing.' },
      pll: { title: 'Perfect PLL Execution', description: 'Practice each PLL until you can execute in under 1 second. Focus on finger tricks.' },
      lookahead: { title: 'Build Lookahead Skills', description: 'Do slow solves tracking the next piece while solving current one. Never pause to search.' }
    };

    if (weaknesses[0].score < 60) {
      recommendations.push(advice[weaknesses[0].key]);
    }

    if (consistency < 70) {
      recommendations.push({ title: 'Improve Consistency', description: `Your consistency is ${consistency.toFixed(0)}%. Focus on smooth, consistent solves rather than rushing.` });
    }

    if (avgTime > 60000) {
      recommendations.push({ title: 'Master Fundamentals', description: 'Focus on intuitive F2L and 2-look OLL/PLL. Use the Beginner Training Plan.' });
    } else if (avgTime > 30000) {
      recommendations.push({ title: 'Advance to CFOP', description: 'Start learning full OLL and PLL. Work on X-Cross planning. Use the Intermediate Plan.' });
    } else if (avgTime > 15000) {
      recommendations.push({ title: 'Optimize Techniques', description: 'Focus on advanced F2L, lookahead, and sub-1s PLLs. Use the Advanced Plan.' });
    } else {
      recommendations.push({ title: 'Elite Training', description: 'Focus on advanced lookahead, maximizing TPS, and competition simulation.' });
    }

    return recommendations.slice(0, 3);
  };

  const getDefaultAnalysis = () => ({
    cross: 50, f2l: 50, oll: 50, pll: 50, lookahead: 50,
    recommendations: [{ title: 'Complete More Solves', description: 'We need more data to give you personalized recommendations.' }],
    stats: { avgTime: '-', bestTime: '-', consistency: '-', solvesAnalyzed: 0 }
  });

  const SkillBar = ({ label, score, icon }) => (
    <div className="skill-bar">
      <div className="skill-header">
        <span className="skill-icon">{icon}</span>
        <span className="skill-label">{label}</span>
        <span className="skill-score">{Math.round(score)}%</span>
      </div>
      <div className="skill-track">
        <div className="skill-fill" style={{ width: `${score}%`, background: score > 70 ? 'var(--success)' : score > 40 ? '#f59e0b' : 'var(--error)' }}></div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Skill Diagnosis</h1>
        <p className="page-subtitle">Analyze your solving patterns and get personalized recommendations</p>
      </div>

      <div className="diagnosis-card">
        <div className="diagnosis-intro">
          <h3>🔬 How it works</h3>
          <p>We analyze your recent solves to estimate skill levels in each solving phase and identify areas for improvement.</p>
          <p className="solve-count">You have <strong>{solveCount}</strong> solves recorded. {solveCount < 5 ? 'Complete at least 5 solves to run diagnosis.' : ''}</p>
        </div>

        <button className="btn btn-primary" onClick={runDiagnosis} disabled={isAnalyzing || solveCount < 5} style={{ width: '100%' }}>
          {isAnalyzing ? '🔄 Analyzing...' : '🔍 Run Diagnosis'}
        </button>
      </div>

      {diagnosisResults && (
        <div className="diagnosis-results">
          <div className="results-header">
            <h3>📊 Analysis Results</h3>
            <p>Based on {diagnosisResults.stats.solvesAnalyzed} recent solves</p>
          </div>

          <div className="stats-summary">
            <div className="stat-card">
              <div className="stat-label">Average</div>
              <div className="stat-value">{diagnosisResults.stats.avgTime}s</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Best</div>
              <div className="stat-value">{diagnosisResults.stats.bestTime}s</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Consistency</div>
              <div className="stat-value">{diagnosisResults.stats.consistency}%</div>
            </div>
          </div>

          <div className="skills-section">
            <h4>Skill Breakdown</h4>
            <SkillBar label="Cross" score={diagnosisResults.cross} icon="✚" />
            <SkillBar label="F2L" score={diagnosisResults.f2l} icon="🔲" />
            <SkillBar label="OLL" score={diagnosisResults.oll} icon="👁️" />
            <SkillBar label="PLL" score={diagnosisResults.pll} icon="🔄" />
            <SkillBar label="Lookahead" score={diagnosisResults.lookahead} icon="👀" />
          </div>

          <div className="recommendations-section">
            <h4>💡 Recommendations</h4>
            {diagnosisResults.recommendations.map((rec, idx) => (
              <div key={idx} className="recommendation-card">
                <h5>{rec.title}</h5>
                <p>{rec.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// SETTINGS PAGE
// ============================================
function SettingsPage() {
  const { user, isAuthenticated, signOut } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleExport = async () => {
    const data = await storage.exportData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `solvesmarter-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const success = await storage.importData(data);
      if (success) {
        alert('Data imported successfully! Refresh the page to see changes.');
      } else {
        alert('Failed to import data. Please check the file format.');
      }
    } catch (err) {
      alert('Error reading file: ' + err.message);
    }
  };

  const handleClearData = async () => {
    if (confirm('Are you sure you want to clear ALL data? This cannot be undone.')) {
      if (confirm('This will delete all solves, learned algorithms, and practice times. Continue?')) {
        await storage.clearAllData();
        alert('All data cleared. Refresh the page.');
      }
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Manage your account and data</p>
      </div>

      <div className="settings-section">
        <h3>👤 Account</h3>
        {isAuthenticated ? (
          <div className="account-card">
            <div className="account-header">
              <div className="account-avatar">{user.email[0].toUpperCase()}</div>
              <div className="account-info">
                <h3>{user.email}</h3>
                <p>Signed in • Data syncing enabled</p>
              </div>
            </div>
            <button className="btn btn-secondary" onClick={signOut} style={{ marginTop: '1rem' }}>Sign Out</button>
          </div>
        ) : (
          <div className="account-card">
            <p>Sign in to sync your data across devices and compete on the leaderboard.</p>
            <button className="btn btn-primary" onClick={() => setShowAuthModal(true)}>Sign In</button>
          </div>
        )}
      </div>

      <div className="settings-section">
        <h3>💾 Data Management</h3>
        <div className="settings-buttons">
          <button className="btn btn-secondary" onClick={handleExport}>📤 Export Data</button>
          <label className="btn btn-secondary">
            📥 Import Data
            <input type="file" accept=".json" onChange={handleImport} style={{ display: 'none' }} />
          </label>
          <button className="btn btn-danger" onClick={handleClearData}>🗑️ Clear All Data</button>
        </div>
      </div>

      <div className="settings-section">
        <h3>ℹ️ About</h3>
        <p>SolveSmarter v2.0</p>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          A training tool for speedcubers. Track solves, learn algorithms, and improve your times with structured practice plans.
        </p>
      </div>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}

// ============================================
// LEADERBOARD PAGE (Placeholder)
// ============================================
function LeaderboardPage() {
  const { isAuthenticated } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Leaderboard</h1>
        <p className="page-subtitle">Compete with other cubers worldwide</p>
      </div>

      <div className="leaderboard-placeholder">
        <div className="placeholder-icon">🏆</div>
        <h3>Coming Soon</h3>
        <p>The global leaderboard is being set up. Sign in to be ready when it launches!</p>
        {!isAuthenticated && (
          <button className="btn btn-primary" onClick={() => setShowAuthModal(true)}>Sign In to Compete</button>
        )}
      </div>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}

// ============================================
// MAIN APP COMPONENT
// ============================================
function AppContent() {
  const [currentPage, setCurrentPage] = useState('timer');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [userLevel, setUserLevel] = useState('beginner');
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const loadLevel = async () => {
      const level = await storage.loadUserLevel();
      setUserLevel(level);
    };
    loadLevel();
  }, []);

  useEffect(() => {
    storage.saveUserLevel(userLevel);
  }, [userLevel]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  const navItems = [
    { id: 'timer', icon: '⏱️', label: 'Timer' },
    { id: 'algorithms', icon: '📚', label: 'Algorithms' },
    { id: 'practice', icon: '🎯', label: 'Practice' },
    { id: 'progress', icon: '📈', label: 'Progress' },
    { id: 'diagnosis', icon: '🔬', label: 'Diagnosis' },
    { id: 'leaderboard', icon: '🏆', label: 'Leaderboard' },
    { id: 'settings', icon: '⚙️', label: 'Settings' },
  ];

  const renderPage = () => {
    switch (currentPage) {
      case 'timer': return <Timer />;
      case 'algorithms': return <AlgorithmsPage />;
      case 'practice': return <PracticePage userLevel={userLevel} setUserLevel={setUserLevel} />;
      case 'progress': return <ProgressPage />;
      case 'diagnosis': return <DiagnosisPage />;
      case 'leaderboard': return <LeaderboardPage />;
      case 'settings': return <SettingsPage />;
      default: return <Timer />;
    }
  };

  return (
    <div className="app-container">
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="logo">SolveSmarter</div>
          <button className="theme-toggle" onClick={toggleTheme}>
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
        </div>

        <SyncStatus onSignInClick={() => setShowAuthModal(true)} />

        <nav className="nav-section">
          {navItems.map(item => (
            <div
              key={item.id}
              className={`nav-item ${currentPage === item.id ? 'active' : ''}`}
              onClick={() => setCurrentPage(item.id)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span>{item.label}</span>
            </div>
          ))}
        </nav>
      </aside>

      <main className="main-content">
        {renderPage()}
      </main>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
